// src/App.jsx
import React, { Suspense, lazy } from "react";
import {
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";

import { useNavVariant } from "./utils/navigation/useNavVariant";

import NavBar from "./components/navigation/NavBar";
import WebsiteNavBar from "./components/navigation/WebsiteNavBar";

import { firebaseApp } from "./firebase/firebaseApp";
import { getAuth } from "firebase/auth";
import { getStorage } from "firebase/storage";
import { getFirestore } from "firebase/firestore";

import RespiratoryRateTool from "./routes/RespiratoryRateTool";
import { RR_LEGAL_VERSION } from "./legal/legalVersion";

// Lazy pages
const LandingPage = lazy(() => import("./pages/LandingPage"));
const LegalPage = lazy(() => import("./pages/LegalPage"));
const RRConsentPage = lazy(() => import("./pages/RRConsentPage"));

function hasValidConsent() {
  const accepted =
    window.localStorage.getItem("rr-consent-accepted") === "true";
  const version = window.localStorage.getItem("rr-consent-version");
  return accepted && version === RR_LEGAL_VERSION;
}

function ConsentGate({ children }) {
  const location = useLocation();

  // Important: do not run localStorage access during SSR (not your case),
  // but this is safe for CRA/Vite client-only apps.
  const ok = hasValidConsent();

  if (!ok) {
    // Send them to consent and remember where they were going
    return (
      <Navigate to="/consent" replace state={{ from: location.pathname }} />
    );
  }

  return children;
}
export default function App() {
  const auth = getAuth(firebaseApp);
  const storage = getStorage(firebaseApp);
  const db = getFirestore(firebaseApp);
  const navigate = useNavigate();

  const navVariant = useNavVariant(); // 'main' | 'landing'

  return (
    <div className="h-[100dvh] w-full flex flex-col typography">
      {navVariant === "main" ? <NavBar /> : <WebsiteNavBar />}

      <div className="h-full w-full flex md:items-start md:justify-center">
        <Suspense fallback={<div className="m-auto">Loading…</div>}>
          <Routes>
            {/* Public */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/legal" element={<LegalPage />} />

            {/* Consent page (normal page, full styling) */}
            <Route path="/consent" element={<RRConsentPage />} />

            {/* RR tool behind consent guard */}
            <Route
              path="/recording/*"
              element={
                <ConsentGate>
                  <RespiratoryRateTool
                    auth={auth}
                    storage={storage}
                    db={db}
                    variant={"RecordingRespiratoryRate"}
                    filePathTag="breatheultra/"
                    usePublicAssets={true}
                    showSignalChart={true}
                    showRRCount={true}
                    showRRCalc={true}
                    debugTracking={false}
                  />
                </ConsentGate>
              }
            />

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </div>
    </div>
  );
}
